import React from 'react';
import SafeIcon from '../../../common/SafeIcon';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';
import { currencies } from '../../../data/settingsData';

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

export default function AccountingTab({ data, onChange }) {
  const handleChange = (e) => {
    const { name, value } = e.target;
    onChange({ [name]: value });
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-400">
      <div className="mb-10">
        <h2 className="text-xl font-bold text-slate-900 mb-2">Financial Preferences</h2>
        <p className="text-sm text-slate-500">Configure how your books are managed and reported.</p>
      </div>

      <div className="mb-10">
        <label className="block text-[11px] font-black text-slate-400 mb-4 uppercase tracking-[0.2em]">Accounting Method</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button 
            onClick={() => onChange({ accounting_method: 'cash' })}
            className={`p-6 text-left border-2 rounded-[24px] transition-all relative overflow-hidden group ${
              data.accounting_method === 'cash' 
                ? 'border-blue-600 bg-blue-50/50 shadow-sm' 
                : 'border-slate-100 bg-slate-50/50 hover:border-slate-200'
            }`}
          >
            {data.accounting_method === 'cash' && (
              <div className="absolute top-3 right-3 text-blue-600">
                <SafeIcon icon={FiInfo} />
              </div>
            )}
            <div className={`font-bold text-sm mb-1 ${data.accounting_method === 'cash' ? 'text-blue-900' : 'text-slate-900'}`}>Cash Basis</div>
            <p className="text-xs text-slate-500 leading-relaxed">Record income when cash is received and expenses when they are paid.</p>
          </button>
          
          <button 
            onClick={() => onChange({ accounting_method: 'accrual' })}
            className={`p-6 text-left border-2 rounded-[24px] transition-all relative overflow-hidden group ${
              data.accounting_method === 'accrual' 
                ? 'border-blue-600 bg-blue-50/50 shadow-sm' 
                : 'border-slate-100 bg-slate-50/50 hover:border-slate-200'
            }`}
          >
            {data.accounting_method === 'accrual' && (
              <div className="absolute top-3 right-3 text-blue-600">
                <SafeIcon icon={FiInfo} />
              </div>
            )}
            <div className={`font-bold text-sm mb-1 ${data.accounting_method === 'accrual' ? 'text-blue-900' : 'text-slate-900'}`}>Accrual Basis</div>
            <p className="text-xs text-slate-500 leading-relaxed">Record income when it's earned and expenses when they are incurred (GAAP standard).</p>
          </button>
        </div>
        
        {data.accounting_method === 'accrual' && (
          <div className="mt-4 p-4 bg-amber-50 border border-amber-100 rounded-2xl flex items-start gap-3 text-amber-800 text-xs font-semibold leading-relaxed">
            <SafeIcon icon={FiAlertTriangle} className="text-lg shrink-0 mt-0.5" />
            <span>Switching to Accrual Basis changes how revenue is recognized. This will impact your Profit & Loss and Balance Sheet statements.</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8 pt-8 border-t border-slate-100">
        <div>
          <label className="block text-[11px] font-black text-slate-400 mb-2 uppercase tracking-[0.2em]">Base Currency</label>
          <div className="relative">
            <select 
              name="currency"
              value={data.currency}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[14px] font-bold focus:ring-2 focus:ring-blue-500/20 outline-none appearance-none"
            >
              {currencies.map(c => <option key={c.code} value={c.code}>{c.code} - {c.name}</option>)}
            </select>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-[11px] font-black text-slate-400 mb-2 uppercase tracking-[0.2em]">Fiscal Year End</label>
          <div className="relative">
            <select 
              name="fiscal_year_end"
              value={data.fiscal_year_end}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[14px] font-bold focus:ring-2 focus:ring-blue-500/20 outline-none appearance-none"
            >
              {months.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-12 p-6 bg-slate-50 rounded-[28px] border border-slate-200/50">
        <label className="flex items-center gap-4 cursor-pointer group">
          <div className="relative inline-block w-12 h-7">
            <input type="checkbox" className="sr-only peer" />
            <div className="w-12 h-7 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-bold text-slate-900 group-hover:text-blue-600 transition-colors">Enable Multi-Currency Support</span>
            <span className="text-xs text-slate-500 font-medium">Handle invoices and payments in foreign currencies with automated exchange rates.</span>
          </div>
        </label>
      </div>
    </div>
  );
}